import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../config';
import { removeTokenFromStorage } from '../store/auth-store';

export const getOrderStatusMessage = (status, paymentMethod) => {
  switch (status) {
    case 0:
      // if (paymentMethod === 1) {
      //   return "Bekliyor";
      // }
      return "Ödeme Başarısız";
    case 2:
      return "Ödeme Alındı";
    case 3:
      return "Onaylanan";
    case 4:
      return "Kargoda";
    case 5:
      return "Tamamlanan";
    case 6:
      return "İptal Edilen";
    default:
      return "Bilinmeyen Durum";
  }
};

export const getOrderStatusClass = (status, paymentMethod) => {
  switch (status) {
    case 0:
      // if (paymentMethod === 1) {
      //   return "pending";
      // }
      return "failed";
    case 2:
      return "paid";
    case 3:
      return "approved";
    case 4:
      return "shipped";
    case 5:
      return "completed";
    case 6:
      return "cancelled";
    default:
      return "unknown";
  }
};

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl(),
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      if (token) {
        headers.set('Authorization', `${token}`);
      }
      if (tkn) {
        headers.set('Tkn', `${tkn}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['User', 'Address', 'ReferralOrders', 'Orders'],
  endpoints: (builder) => ({
    getUser: builder.query({
      query: () => '/user',
      providesTags: ['User'],
      transformResponse: (response) => {
        return { ...response, sifre: "" };
      },
    }),

    updateUser: builder.mutation({
      query: (userData) => {
        const dataToSend = { ...userData };
        if (!dataToSend.sifre || dataToSend.sifre.trim() === '') {
          const { sifre, ...dataWithoutPassword } = dataToSend;
          return {
            url: '/user',
            method: 'PUT',
            body: dataWithoutPassword,
          };
        }

        return {
          url: '/user',
          method: 'PUT',
          body: userData,
        };
      },
      invalidatesTags: ['User'],
    }),

    changePassword: builder.mutation({
      query: ({ oldPassword, newPassword, userData }) => ({
        url: '/user',
        method: 'PUT',
        body: {
          ...userData,
          sifre: newPassword,
          oldPassword: oldPassword
        },
      }),
      invalidatesTags: ['User'],
    }),

    logout: builder.mutation({
      queryFn: () => ({ data: {} }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          removeTokenFromStorage();
          dispatch(userApi.util.resetApiState());
        } catch (error) {
          console.error('Logout failed: ', error);
        }
      },
    }),

    login: builder.mutation({
      query: (credentials) => ({
        url: '/login',
        method: 'POST',
        body: credentials,
      }),
    }),

    getReferralOrders: builder.query({
      query: () => '/referral-orders',
      providesTags: ['ReferralOrders'],
      transformResponse: (data) => {        
        if (!data || !data.codes || typeof data.codes !== 'object') {
          return { 
            orders: [], 
            commission: 0,
            lastPaymentDate: null,
            lastPaymentDateFormatted: "Hiç ödeme yapılmadı",
            salesAfterLastPayment: 0
          };
        }
        
        const formattedOrders = Object.entries(data.codes).map(([refCode, refData]) => {
          
          if (!refData || !refData.products || !Array.isArray(refData.products)) {
            return {
              id: refCode,
              referral_code: refCode,
              products: [],
              order_date_timestamp: 0
            };
          }

          // timestamp bilgisini direk refData'dan alıyoruz
          return {
            id: refCode,
            referral_code: refCode,
            order_date_timestamp: refData.order_date_timestamp || 0,
            products: refData.products.map(product => ({
              product_image: product?.image || '',
              product_title: product?.title || 'Ürün Adı Yok',
              total_amount: product?.price || 0
            }))
          };
        });

        // Format tarihi Türkçe gösterimi için
        let lastPaymentDateFormatted = "Hiç ödeme yapılmadı";
        if (data.son_odeme_tarihi) {
          try {
            const date = new Date(data.son_odeme_tarihi);
            lastPaymentDateFormatted = date.toLocaleDateString('tr-TR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          } catch (e) {
            console.error("Tarih formatlanırken hata:", e);
          }
        }

        return {
          orders: formattedOrders,
          commission: data.komisyon || 0,
          lastPaymentDate: data.son_odeme_tarihi || null,
          lastPaymentDateFormatted,
          salesAfterLastPayment: data.son_odeme_sonrasi_satis || 0,
          totalSales: data.toplam_satis || 0
        };
      },
    }),

    createReferralCode: builder.mutation({
      query: (customCode) => ({
        url: '/cart/share',
        method: 'POST',
        body: {
          share_code: customCode ? customCode.toUpperCase() : undefined,
          type: "unlimited"
        },
      }),
    }),

    getAddresses: builder.query({
      query: () => '/addresses',
      providesTags: ['Address'],
    }),

    addAddress: builder.mutation({
      query: (addressData) => ({
        url: '/addresses',
        method: 'POST',
        body: addressData,
      }),
      invalidatesTags: ['Address'],
    }),

    updateAddress: builder.mutation({
      query: ({ addressId, addressData }) => ({
        url: `/addresses/${addressId}`,
        method: 'PUT',
        body: addressData,
      }),
      invalidatesTags: ['Address'],
    }),

    deleteAddress: builder.mutation({
      query: (addressId) => ({
        url: `/addresses/${addressId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Address'],
    }),

    getOrders: builder.query({
      query: () => '/orders',
      providesTags: ['Orders'],
      transformResponse: (response) => {
        if (!Array.isArray(response)) {
          console.warn("'getOrders' response is not an array:", response);
          return [];
        }
        return response.map(order => {
          const updatedProducts = order.products.map(product => {
            if (typeof product.resim === 'string') {
              try {
                let resimString = product.resim;
                if ((resimString.startsWith("'") && resimString.endsWith("'")) || (resimString.startsWith('"') && resimString.endsWith('"'))) {
                  resimString = resimString.substring(1, resimString.length - 1);
                }
                resimString = resimString.replace(/\\/g, '\\');

                resimString = resimString.replace(/\'/g, "'").replace(/\"/g, '"');

                const imageUrls = JSON.parse(resimString);
                return { ...product, resim: imageUrls };
              } catch (error) {
                console.error('Image JSON parse edilemedi (getOrders):', product.resim, error);
                return { ...product, resim: { i1: 'no-image.png' } };
              }
            }
            return product;
          });

          let fixedDate = order.siparis_tarihi;
          const orderDate = new Date(order.siparis_tarihi);
          if (isNaN(orderDate.getTime()) || orderDate.getFullYear() < 2000) {
            if (typeof order.siparis_tarihi === 'number' && String(order.siparis_tarihi).length <= 10) {
              fixedDate = new Date(order.siparis_tarihi * 1000).toISOString();
            } else if (typeof order.siparis_tarihi === 'string' && /^[0-9]+$/.test(order.siparis_tarihi) && order.siparis_tarihi.length <= 10) {
              fixedDate = new Date(parseInt(order.siparis_tarihi, 10) * 1000).toISOString();
            }
            else {
              console.warn(`Invalid or very old date for order ${order.id}: ${order.siparis_tarihi}. Using current date.`);
              fixedDate = new Date().toISOString();
            }
          } else {
            fixedDate = orderDate.toISOString();
          }
          return { ...order, siparis_tarihi: fixedDate, products: updatedProducts };
        });
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useLoginMutation,
  useGetReferralOrdersQuery,
  useCreateReferralCodeMutation,
  useGetAddressesQuery,
  useAddAddressMutation,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
  useGetOrdersQuery,
  useLogoutMutation,
} = userApi;
